import { useGetDashboardShippingHistoryQuery } from "@/services/conciliation-dashboard";
import { useMemo, useState } from "react";
import { FIRST_PAGE, HISTORY_PAGE_SIZE } from "../constants";
import { DateRange, Shipping } from "../types";

const EMPTY_SHIPPINGS: readonly Shipping[] = [];

export interface ShippingHistoryPage {
  rows: Shipping[];
  currentPage: number;
  lastPage: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface ShippingHistoryResult {
  page: ShippingHistoryPage;
  expandedIds: number[];
  toggleExpanded: (shippingId: number) => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}

export const useShippingHistory = (
  range: DateRange
): ShippingHistoryResult => {
  const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const { data } = useGetDashboardShippingHistoryQuery({
    ...range,
    page: currentPage,
    perPage: HISTORY_PAGE_SIZE,
  });

  const page: ShippingHistoryPage = useMemo((): ShippingHistoryPage => {
    const rows: readonly Shipping[] = data?.data ?? EMPTY_SHIPPINGS;
    const lastPage: number = data?.meta.lastPage ?? FIRST_PAGE;

    return {
      rows: [...rows],
      currentPage,
      lastPage,
      hasPrevious: currentPage > FIRST_PAGE,
      hasNext: currentPage < lastPage,
    };
  }, [data, currentPage]);

  const toggleExpanded = (shippingId: number): void => {
    setExpandedIds((current: number[]) =>
      current.includes(shippingId)
        ? current.filter((id: number) => id !== shippingId)
        : [...current, shippingId]
    );
  };

  return {
    page,
    expandedIds,
    toggleExpanded,
    goToPreviousPage: (): void =>
      setCurrentPage((current: number) => Math.max(FIRST_PAGE, current - 1)),
    goToNextPage: (): void => setCurrentPage((current: number) => current + 1),
  };
};
